/* agmf-desktop-lightbox.js — on desktop, a click on a piece of content opens
 * it large (Dorian 2026-09-02).
 *
 * The phone feed is a stack you swipe through, and a card IS the viewing
 * size. The desktop page is a grid of slots, and a slot is a thumbnail: the
 * natural thing to do with one is open it. So on the desktop layout a click on
 * any card or gallery slide opens the piece in a centred dialog - the same
 * glass sheet the article reader uses - with the picture or video large on
 * one side and who-said-it, the words and the numbers on the other.
 *
 * Controls stay controls. The speaker, the progress bar, the close X, the rail
 * arrows, links and buttons all keep doing their own job; only a click on the
 * content itself opens it. Articles already open their own reader, which is
 * left alone. YouTube is re-embedded in the dialog WITH its native controls,
 * because the card's embed is deliberately chrome-less and a large view is
 * exactly where a shopper wants to scrub.
 *
 * Own file. It reads one class the desktop layout sets on <html>
 * (`agdesk-on`) and one supported hook the engine publishes
 * (window.AGMF.itemFor). Off desktop it never claims a click.
 */
(function () {
  'use strict';
  var root = document.documentElement;
  // .agmf-ytshield--brand is the patch over YouTube's "Watch on YouTube"
  // corner. It is a BLOCK, not a door: opening the dialog from it would make
  // the one place we cover the easiest place to click.  The --top band is
  // deliberately absent, because that one IS the way in.
  // .agmf-ytc is the card's own control row (v40.1) - the whole strip, not
  // just its buttons, so the gap between them is dead space rather than a
  // door into the dialog.
  var CONTROL = '.agmf-ytc, .agmf-sound, [data-agmf-ytsound], [data-agmf-ytbar], [data-agmf-ytclose], ' +
                '.agmf-ytshield--brand, ' +
                '.agmf-arrow, .agmf-foot-cue, a, button, input, select, textarea, label, ' +
                '.agmf-sugg-tile, .agmf-sugg-row, .agmf-acc, .agmf-cap';
  var SKIP_CARD = '.agmf-card--article, .agmf-suggest, .agmf-spec';

  function desktop() { return root.classList.contains('agdesk-on'); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmtCount(n) {
    n = Number(n) || 0;
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  function ytEmbed(id) {
    // THE DIALOG KEEPS YOUTUBE'S OWN BAR, and it is the only player here that
    // does. On a card the bar had to go: it swallowed the click that opens
    // the picture. Here the picture is already open, so a click meaning
    // play/pause is what a mouse means everywhere - and the native bar brings
    // a real scrub and a real volume with it, which our row does not.
    //
    // `fs=0` because this IS the large view; the two remaining ways off the
    // site (the title strip that fades in at the top, and the wordmark at the
    // right end of the bar) are covered by the shields below. Covering is not
    // as good as removing - a cross-origin player cannot be edited, only
    // hidden behind glass - so the geometry is generous on purpose.
    return 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
      '?autoplay=1&mute=1&playsinline=1&rel=0&iv_load_policy=3&fs=0' +
      '&modestbranding=1&controls=1' +
      '&enablejsapi=1&origin=' + encodeURIComponent(location.origin);
  }

  // The engine's message walker only knows the cards' own frames, so the
  // dialog listens for its own and republishes state as the same DOM event
  // the rest of the page already speaks (agmf:ytstate).
  function listenYt(dlg) {
    var frame = dlg.querySelector('.agmf-lb-frame');
    if (!frame) return;
    var last = null;
    var onMsg = function (e) {
      if (typeof e.data !== 'string' || e.data.charAt(0) !== '{') return;
      if (String(e.origin).indexOf('youtube') === -1) return;
      if (!frame.contentWindow || e.source !== frame.contentWindow) return;
      var data; try { data = JSON.parse(e.data); } catch (err) { return; }
      var info = data && data.info;
      if (!info || typeof info.playerState !== 'number' || info.playerState === last) return;
      last = info.playerState;
      var NAMED = { '-1': 'unstarted', 0: 'ended', 1: 'play', 2: 'pause', 3: 'buffering', 5: 'cued' };
      try {
        dlg.dispatchEvent(new CustomEvent('agmf:ytstate', {
          bubbles: true,
          detail: {
            state: NAMED[String(info.playerState)] || String(info.playerState),
            at_s: Math.round(info.currentTime || 0),
            dur_s: Math.round(info.duration || 0) || null,
            controls: 'native'
          }
        }));
      } catch (err) {}
    };
    window.addEventListener('message', onMsg);
    // ask the player to start talking; a command before it is ready is a no-op
    var hello = function () {
      try { frame.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*'); } catch (err) {}
    };
    frame.addEventListener('load', hello);
    // Not three guesses at when the player has booted: on a slow link all
    // three land before it can hear, and the dialog's player stays mute
    // (the feed's own players had the same fault, fixed 2026-09-08). Every
    // half second until the first message, a minute at most.
    var heard = false, asks = 0;
    var clock = setInterval(function () {
      if (heard || ++asks > 120) { clearInterval(clock); return; }
      hello();
    }, 500);
    var onFirst = function (e) {
      if (e.source === frame.contentWindow) { heard = true; window.removeEventListener('message', onFirst); }
    };
    window.addEventListener('message', onFirst);
    var off = dlg.__agmfOffResize;
    dlg.__agmfOffResize = function () {
      if (off) off();
      clearInterval(clock);
      window.removeEventListener('message', onMsg);
      window.removeEventListener('message', onFirst);
    };
  }
  function reviewStars(item) {
    var tags = item.tags || [];
    for (var i = 0; i < tags.length; i++) {
      var m = /^([1-5])\/5$/.exec(String(tags[i]));
      if (m) return parseInt(m[1], 10);
    }
    return 0;
  }

  // THE PICTURE KEEPS ITS GROUND (Dorian 2026-10-07: "Same way you did that
  // 3:4 images have a color background card, so also when clicking them the
  // color background should be there, so in case there are transparent
  // pictures it will still look the same"). On the page a cut-out render sits
  // on its slide's sand (#f3f1eb) or its card's grey (#efefec); in here it sat
  // straight on the blurred glow, so the same belt arrived on a dark smear
  // with its own silhouette for a shadow. Measured on the Magnetix page: the
  // first picture is a transparent webp.
  //
  // The colour is READ from the piece that was clicked - the first painted
  // background from the picture up to its slide or card - not restated here,
  // so the page and the dialog cannot drift apart. A photograph is opaque and
  // covers it; only the see-through parts of a render show it.
  function groundOf(host, target) {
    var pic = target && target.tagName === 'IMG' && host.contains(target) ? target
      : host.querySelector('.agmf-hero-slide > img, img.agmf-image, img.agmf-review-img, .agmf-media img:not(.agmf-ytposter)');
    for (var el = pic; el; el = el.parentNode) {
      var bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(bg)) return bg;
      if (el === host) break;
    }
    return '';
  }

  // What to show: from the record when the engine knows one, from the DOM
  // when it does not (a gallery slide has no record, only its picture).
  function describe(host) {
    var item = window.AGMF && window.AGMF.itemFor ? window.AGMF.itemFor(host) : null;
    if (item) {
      var v = item.video || {};
      var src = item.source || {};
      // A LOOP IS A CLIP MADE HERE, whatever it was cut from (Dorian
      // 2026-09-23: "the video clips (loops) should not be youtube anymore").
      // Asked first and by what it IS, so the feed can stop calling loops
      // YouTube without this line falling through to the source's platform.
      var isLoop = item.contentType === 'created-loop' || item.format === 'GIF';
      var kind = isLoop ? 'Product clip'
        : item.platform === 'Reviews' ? 'Verified buyer'
        : item.platform === 'T&E' ? 'T&E Community'
        : item.platform === 'Instagram' ? 'Instagram'
        : item.platform === 'YouTube' ? (item.format === 'Short' ? 'YouTube Short' : item.format === 'GIF' ? 'Product clip' : 'YouTube')
        : item.platform === 'Assets' ? (src.label || 'Agilite') : (src.platform || item.platform || '');
      var media = null;
      if (v.provider === 'youtube' && v.id) media = { yt: v.id, poster: item.thumb || '' };
      else if (v.src) media = { video: v.src, poster: item.format === 'GIF' ? '' : (item.thumb || '') };
      else if (item.thumb) media = { image: item.thumb };
      // A CREATED LOOP SPEAKS THROUGH ITS CAPTION. Its record title carries
      // an internal " - loop" suffix and its `text` is the library's own
      // description ("Muted video loop from 0:00.0 to 0:05.0; stored as
      // video-backed GIF media") - neither is for a shopper (Dorian
      // 2026-09-02). The words the card paints are the caption's parts, so
      // the dialog uses those and nothing else from the record.
      var isLoop = item.contentType === 'created-loop' || item.format === 'GIF' || !!(v.loop);
      var cap = item.caption || null;
      var capParts = cap && cap.parts ? cap.parts : {};
      var capText = function (name) {
        var part = capParts[name];
        if (!part) return '';
        return String(part.text || (part.html ? part.html.replace(/<[^>]+>/g, '') : '') || '').trim();
      };
      var loopTitle = isLoop ? (capText('title') || (cap && cap.text) || '') : (item.title || '');
      var loopText = isLoop ? [capText('kicker'), capText('body')].filter(Boolean).join(' - ') : (item.text || '');
      return {
        media: media,
        ck: item.ck || null,
        contentType: item.contentType || null,
        recordId: item.id != null ? item.id : null,
        vertical: v.vertical === true || item.format === 'Short' || item.format === 'GIF' || item.shape === 'still',
        avatar: src.avatar || '',
        name: src.name || (item.platform === 'Assets' || isLoop ? 'Agilite' : ''),
        kind: kind, date: fmtDate(item.publishedAt),
        title: loopTitle, text: loopText,
        stars: item.platform === 'Reviews' ? reviewStars(item) : 0,
        metrics: item.metrics || null,
        captionHtml: (isLoop && cap && window.AGMF && window.AGMF.captionHtml) ? window.AGMF.captionHtml(item) : ''
      };
    }
    var img = host.querySelector('img:not(.agmf-ytposter)');
    var yt = host.querySelector('[data-agmf-yt]');
    var vid = host.querySelector('video');
    var poster = host.querySelector('.agmf-ytposter');
    var titleEl = document.querySelector('.agmf-hero-title');
    var media2 = null;
    if (yt) media2 = { yt: yt.getAttribute('data-agmf-yt'), poster: poster ? poster.src : '' };
    else if (vid) media2 = { video: vid.getAttribute('src') || vid.getAttribute('data-agmf-src'), poster: vid.poster || '' };
    else if (img) media2 = { image: img.currentSrc || img.src };
    return {
      media: media2,
      vertical: true,
      avatar: '', name: 'Agilite', kind: 'Product', date: '',
      title: titleEl ? titleEl.textContent.trim() : document.title,
      text: '', stars: 0, metrics: null
    };
  }

  var open = null;
  function close() {
    if (!open) return;
    if (open.__agmfOffResize) open.__agmfOffResize();
    open.remove();
    open = null;
    root.classList.remove('agmf-noscroll');
  }

  // ── THE MEDIA IS SIZED HERE, NOT BY THE CASCADE (Dorian 2026-09-02: "the
  // content is putting itself not correctly on the dialog"). Measured on the
  // shipped page: a YouTube shell came out 601x1069 inside a 601x751 stage -
  // 318px taller than the box that holds it, its middle pushed 159px down, so
  // the player's own controls sat off the bottom edge.
  //
  // WHY the CSS could not do it: the stage is `display:grid; place-items:
  // center`, so its single implicit ROW is auto-sized by its child. A child
  // asking for `height:100%` against an auto row has nothing to resolve
  // against, falls back to auto, and an `aspect-ratio` then blows the box up
  // from the width. The stage's own height IS definite (the sheet fixes it) -
  // the child just could not see it.
  //
  // So: measure the stage, take the media's real ratio, and write the box's
  // px size. "Contain" in eleven lines, and it holds for every kind at once -
  // a 1:1 render, a 9:16 Short, a 16:9 film, a clip whose caption overlay has
  // to sit on THE CLIP and not on the letterbox beside it.
  function ratioOf(el, dlg) {
    if (!el) return 0;
    if (el.tagName === 'IMG') return el.naturalWidth && el.naturalHeight ? el.naturalWidth / el.naturalHeight : 0;
    if (el.tagName === 'VIDEO') {
      if (el.videoWidth && el.videoHeight) return el.videoWidth / el.videoHeight;
      return 0;                      // wait for metadata rather than guess
    }
    // an iframe has no intrinsic size: the piece told us whether it stands up
    return dlg && dlg.classList.contains('agmf-lb--tall') ? 9 / 16 : 16 / 9;
  }

  function fit(dlg) {
    if (!dlg || !dlg.isConnected) return;
    var stage = dlg.querySelector('.agmf-lb-stage');
    var box = dlg.querySelector('.agmf-lb-media') ||
              dlg.querySelector('.agmf-lb-img, .agmf-lb-video, .agmf-lb-frame');
    if (!stage || !box) return;
    var el = box.classList.contains('agmf-lb-media')
      ? box.querySelector('video, img, iframe') : box;
    var sw = stage.clientWidth, sh = stage.clientHeight;
    var r = ratioOf(el, dlg);
    if (!sw || !sh || !r) return;
    var w = sw, h = sw / r;
    if (h > sh) { h = sh; w = sh * r; }
    box.style.width = Math.round(w) + 'px';
    box.style.height = Math.round(h) + 'px';
  }

  // The ratio arrives late for anything that has to load. Re-fit on every
  // event that can reveal it, and on a resize.
  function armFit(dlg) {
    fit(dlg);
    requestAnimationFrame(function () { fit(dlg); });
    var el = dlg.querySelector('.agmf-lb-img, .agmf-lb-video');
    if (el) {
      el.addEventListener('load', function () { fit(dlg); });
      el.addEventListener('loadedmetadata', function () { fit(dlg); });
      if (el.tagName === 'IMG' && el.complete) fit(dlg);
    }
    // a clip that never reports its size would fall back to the cascade, and
    // the cascade is what was broken: after 700ms take the card's own 3:4.
    setTimeout(function () {
      if (!dlg.isConnected) return;
      var v = dlg.querySelector('.agmf-lb-video');
      if (v && !v.videoWidth) {
        var b = dlg.querySelector('.agmf-lb-media') || v;
        var st = dlg.querySelector('.agmf-lb-stage');
        if (st && !b.style.height) {
          var h = st.clientHeight, w = h * 0.75;
          if (w > st.clientWidth) { w = st.clientWidth; h = w / 0.75; }
          b.style.width = Math.round(w) + 'px';
          b.style.height = Math.round(h) + 'px';
        }
      }
    }, 700);
    var onResize = function () { fit(dlg); };
    window.addEventListener('resize', onResize);
    dlg.__agmfOffResize = function () { window.removeEventListener('resize', onResize); };
  }

  function show(d) {
    close();
    if (!d || !d.media) return;
    var m = d.media, stage;
    if (m.yt) {
      stage = '<div class="agmf-lb-media agmf-lb-media--yt">' +
        '<iframe class="agmf-lb-frame" src="' + esc(ytEmbed(m.yt)) +
        '" allow="autoplay; encrypted-media; picture-in-picture" title="Video"></iframe>' +
        '<span class="agmf-ytshield agmf-ytshield--top" aria-hidden="true"></span>' +
        '<span class="agmf-ytshield agmf-ytshield--brand" aria-hidden="true"></span>' +
        '</div>';
    } else if (m.video) {
      stage = '<video class="agmf-lb-video" src="' + esc(m.video) + '"' +
        (m.poster ? ' poster="' + esc(m.poster) + '"' : '') +
        ' controls autoplay muted loop playsinline></video>';
      // the caption rides the clip in its own box, so its sizes (cqh) read
      // the clip's height, exactly as they do on the card
      if (d.captionHtml) stage = '<div class="agmf-lb-media">' + stage + d.captionHtml + '</div>';
    } else {
      stage = '<img class="agmf-lb-img" src="' + esc(m.image) + '" alt=""' +
        (d.ground ? ' style="background-color:' + esc(d.ground) + '"' : '') + '>';
    }
    var stars = '';
    if (d.stars) { for (var i = 0; i < 5; i++) stars += i < d.stars ? '★' : '☆'; }
    var nums = [];
    if (d.metrics) {
      if (d.metrics.views) nums.push(fmtCount(d.metrics.views) + ' views');
      if (d.metrics.likes) nums.push(fmtCount(d.metrics.likes) + ' likes');
      if (d.metrics.comments) nums.push(fmtCount(d.metrics.comments) + ' comments');
    }
    var avatar = d.avatar
      ? '<img class="agmf-lb-avatar" src="' + esc(d.avatar) + '" alt="">'
      : '<span class="agmf-lb-avatar agmf-lb-avatar--mono">' + esc((d.name || 'A').charAt(0).toUpperCase()) + '</span>';
    var head = '<div class="agmf-lb-head">' + avatar +
      '<span class="agmf-lb-who"><strong dir="auto">' + esc(d.name) + '</strong>' +
      '<small>' + esc(d.kind) + (d.date ? ' · ' + esc(d.date) : '') + '</small></span></div>';
    var body =
      (stars ? '<div class="agmf-lb-stars" aria-label="Rated ' + d.stars + ' out of 5">' + stars + '</div>' : '') +
      (d.title ? '<h3 dir="auto">' + esc(d.title) + '</h3>' : '') +
      (d.text ? '<p dir="auto">' + esc(d.text) + '</p>' : '') +
      (nums.length ? '<div class="agmf-lb-nums">' + esc(nums.join(' · ')) + '</div>' : '');
    // LIQUID GLASS, not a black box (Dorian 2026-09-02). The stage used to be
    // flat #0d0d0d, so a product render on its sand ground sat inside two
    // black bands like a broken slideshow. Now the picture's own colours,
    // blurred wide, fill the stage behind it - and when a piece has nothing
    // to say (a render: no words, no numbers) the side panel does not stand
    // there empty; who-and-what becomes a glass bar over the picture.
    var glowSrc = m.image || m.poster || '';
    var glow = glowSrc ? '<div class="agmf-lb-glow" style="background-image:url(' + esc(glowSrc) + ')"></div>' : '';
    // ONE DIALOG FOR EVERY PIECE (Dorian 2026-09-03, on the product card's own
    // picture: "the visual of clicking that card should not be different of
    // the others - the fact that it is a product card is relevant only for
    // the mobile version, in desktop it should be really uniform").
    // A piece with nothing to say used to fold the side panel into a glass
    // bar over the picture, which made exactly one kind of content - the
    // product's own photographs - open in a different shape from everything
    // around it. The panel now always stands, carrying whatever is known.
    var bar = '';
    var dlg = document.createElement('div');
    dlg.className = 'agmf-lb' + (d.vertical ? ' agmf-lb--tall' : '');
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.innerHTML =
      '<div class="agmf-lb-scrim" data-agmf-lb-close></div>' +
      '<div class="agmf-lb-sheet">' +
        '<button class="agmf-lb-close" type="button" data-agmf-lb-close aria-label="Close">&#10005;</button>' +
        '<div class="agmf-lb-stage">' + glow + stage + bar + '</div>' +
        '<aside class="agmf-lb-side">' + head + '<div class="agmf-lb-body">' + body + '</div></aside>' +
      '</div>';
    // WHAT IS IN HERE, FOR ANYTHING THAT COUNTS. The dialog holds no card and
    // no slide, so a tracker walking `.agmf-card` found nothing: time spent
    // on the biggest view of a piece, and every click inside it, arrived with
    // no content id at all. The record was resolved a moment ago in
    // describe(); this is simply where it is written down.
    if (d.ck) dlg.dataset.agmfTrackCk = d.ck;
    if (d.contentType) dlg.dataset.agmfTrackType = d.contentType;
    if (d.recordId != null) dlg.dataset.agmfId = String(d.recordId);
    document.body.appendChild(dlg);
    root.classList.add('agmf-noscroll');
    open = dlg;
    armFit(dlg);          // the box is measured, never guessed by the cascade
    if (m.yt) listenYt(dlg);
  }

  // Capture phase, so this runs before the engine's own card handlers - and it
  // only ever CLAIMS a click nothing else wanted: never a control, never a
  // link, never the second half of a double click, never an article (it has
  // its own reader), and never off the desktop layout.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-agmf-lb-close]')) { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (open) { if (!open.contains(e.target)) close(); return; }
    if (!desktop() || e.detail >= 2) return;
    var ctrl = e.target.closest(CONTROL);
    if (ctrl) {
      // A LINK ROUND A PICTURE IS NOT A CONTROL. The library wrapped
      // Instagram stills in a link to the post; the engine no longer emits
      // that, and no card may leave the site at all - so if a wrapper like it
      // ever returns, the picture still opens here rather than being skipped
      // as somebody's button.
      var wrapsMedia = ctrl.tagName === 'A' &&
        e.target.closest('.agmf-media, img, video, [data-agmf-yt]') &&
        ctrl.closest('.agmf-card, .agmf-hero-slide');
      if (!wrapsMedia) return;
    }
    var host = e.target.closest('.agmf-hero-slide, .agmf-card');
    if (!host) return;
    if (host.matches(SKIP_CARD) || host.closest(SKIP_CARD)) return;
    // On a feed card, only the CONTENT opens - not the head, not the caption.
    // Content is the picture or the player itself, wherever the renderer put
    // it: a community photo is an <img> straight inside the card, with no
    // .agmf-media around it, and 22 of 46 cards refused the click for exactly
    // that reason (measured 2026-09-02). The head and its avatar stay out.
    if (host.classList.contains('agmf-card')) {
      if (e.target.closest('.agmf-head, .agmf-avatar, .agmf-ig-cap, .agmf-caption, .agmf-yt-under')) return;
      if (!e.target.closest('.agmf-media, .agmf-review-hero, .agmf-review-copy, img, video, [data-agmf-yt]')) return;
    }
    var d = describe(host);
    if (!d || !d.media) return;
    if (d.media.image) d.ground = groundOf(host, e.target);
    e.preventDefault();
    e.stopPropagation();
    // a double click is an agilike, not an open: the engine's tap arbiter
    // holds the open 330ms and drops it when the like lands (2026-09-06)
    if (window.AGMF_deferTap) window.AGMF_deferTap(host, function () { show(d); });
    else show(d);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (open && (e.key === 'Escape' || e.key === 'Esc')) close();
  });
}());
